import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import {
  Budget,
  BudgetSums,
  Reconciliation,
  Sheet,
  SheetCategory,
  Spend,
  SpendCreate,
} from '../../../../lib/Types'
import { mapWithConcurrencyLimit } from '../../../../lib/Concurrency'
import * as BudgetAPI from '../API/BudgetAPI'
import * as CategoriesAPI from '../API/CategoriesAPI'
import * as SheetAPI from '../API/SheetAPI'
import * as SpendAPI from '../API/SpendAPI'
import { DistributionType } from '../Enums/DistributionType'
import { SheetType } from '../Enums/SheetType'
import { SpendType } from '../Enums/SpendType'

interface UseBudgetSumsResult {
  data?: BudgetSums
}

export const useCreateBudget = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => BudgetAPI.createBudget(),
    onSuccess: (newSheet: Sheet) => {
      queryClient.invalidateQueries({ queryKey: ['task'] })
      queryClient.setQueryData(['budgets'], (oldData: Sheet[]) => [
        ...oldData,
        newSheet,
      ])
    },
  })
}

type SpendClonePayload = SpendCreate & {
  description?: string
  distribution_type?: DistributionType
  distribution_primary_user?: number
  distribution_secondary_user?: number
}

const buildSpendClonePayload = (
  spend: Spend,
  newCategoriesById: Map<number, SheetCategory>,
  parentId?: number,
): SpendClonePayload => {
  const payload: SpendClonePayload = {
    title: spend.title,
    description: spend.description,
    type: spend.type,
    variant: spend.variant,
    accounting_date: new Date(spend.accounting_date),
    amounts: spend.amounts.map(({ formula, frequency }) => ({
      formula,
      frequency,
    })),
  }

  if (spend.type === SpendType.EXPENSE) {
    payload.category = newCategoriesById.get(spend.category.id)
  } else {
    payload.distribution_type = spend.distribution_type
    payload.distribution_primary_user = spend.distribution_primary_user
    payload.distribution_secondary_user = spend.distribution_secondary_user
  }

  if (parentId) {
    payload.parent_id = parentId
  }

  return payload
}

// Caps how many create-category/create-spend requests are in flight at
// once, so cloning a large budget doesn't exhaust the backend's DB
// connection pool.
const CLONE_REQUEST_CONCURRENCY = 4

// Clones a budget by reusing the existing create-budget and add-spend
// endpoints: an empty budget is created, then the source budget's spends
// are replayed onto it (parents first, so children can reference the
// newly created parent ids).
const cloneBudget = async (budget: Budget): Promise<Budget> => {
  const newBudget = await BudgetAPI.createBudget()

  const [incomes, expenses, sourceCategories]: [
    Spend[],
    Spend[],
    SheetCategory[],
  ] = await Promise.all([
    SpendAPI.getIncomes(budget),
    SpendAPI.getExpenses(budget),
    CategoriesAPI.getCategories(budget),
  ])

  const categoryPairs = await mapWithConcurrencyLimit(
    sourceCategories,
    CLONE_REQUEST_CONCURRENCY,
    async (category) => {
      const newCategory = await CategoriesAPI.addCategory(newBudget, {
        name: category.name,
        budget: newBudget.id,
      })
      return [category.id, newCategory] as const
    },
  )
  const newCategoriesById = new Map(categoryPairs)

  const sourceSpends = [...incomes, ...expenses]
  const parents = sourceSpends.filter((spend) => !spend.parent_id)
  const children = sourceSpends.filter((spend) => spend.parent_id)

  const parentIdPairs = await mapWithConcurrencyLimit(
    parents,
    CLONE_REQUEST_CONCURRENCY,
    async (parent) => {
      const newSpend = await SpendAPI.addItem(
        SheetType.BUDGET,
        newBudget.id,
        buildSpendClonePayload(parent, newCategoriesById),
      )
      return [parent.id, newSpend.id] as const
    },
  )
  const newParentIds = new Map(parentIdPairs)

  await mapWithConcurrencyLimit(
    children,
    CLONE_REQUEST_CONCURRENCY,
    async (child) => {
      const newParentId = newParentIds.get(child.parent_id!)

      if (!newParentId) {
        return
      }

      await SpendAPI.addItem(
        SheetType.BUDGET,
        newBudget.id,
        buildSpendClonePayload(child, newCategoriesById, newParentId),
      )
    },
  )

  const clonedBudget: Budget = {
    ...newBudget,
    name: `${budget.name || `Budget #${budget.id}`} (kopi)`,
  }
  await SheetAPI.saveSheet(clonedBudget)

  return clonedBudget
}

export const useCloneBudget = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (budget: Budget) => cloneBudget(budget),
    onSuccess: (newSheet: Sheet) => {
      queryClient.invalidateQueries({ queryKey: ['task'] })
      queryClient.setQueryData(['budgets'], (oldData: Sheet[]) => [
        ...oldData,
        newSheet,
      ])
    },
  })
}

export const useReconciliationExpenses = (
  budget: Budget,
  year: number,
): UseQueryResult<Spend[]> => {
  return useQuery({
    queryKey: ['budgets', budget.id, 'reconciliation', year],
    queryFn: () => SpendAPI.getReconiliationExpenses(budget, year),
    staleTime: Infinity,
  })
}

export const useBudgetSums = (budget: Budget): UseBudgetSumsResult => {
  return useQuery({
    queryKey: ['budgets', budget.id, 'sums'],
    queryFn: () => BudgetAPI.getBudgetSums(budget),
    staleTime: Infinity,
  })
}

export const useBudget = (): UseQueryResult<Budget> => {
  const params = useParams()

  if (!params.budgetId) {
    throw Error('Budget ID is missing from params')
  }

  const budgetId = parseInt(params.budgetId, 10)

  return useQuery({
    queryKey: ['budgets', budgetId],
    queryFn: () => BudgetAPI.getBudget(budgetId),
    staleTime: Infinity,
  })
}

export const useBudgets = (): UseQueryResult<Budget[]> => {
  return useQuery({
    queryKey: ['budgets'],
    queryFn: () => BudgetAPI.getBudgets(),
    staleTime: Infinity,
  })
}

export const useBudgetReconciliations = (
  budget: Budget,
  year: number,
): UseQueryResult<Reconciliation[]> => {
  return useQuery({
    queryKey: ['budgets', budget.id, 'reconciliations', year],
    queryFn: () => BudgetAPI.getBudgetReconciliations(budget, year),
    staleTime: Infinity,
  })
}

export const useUpdateBudgetReconciliation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (reconciliation: Reconciliation) =>
      BudgetAPI.saveBudgetReconciliation(reconciliation),
    onSuccess: (savedReconciliation: Reconciliation) => {
      const queryKey = [
        'budgets',
        savedReconciliation.budget_id,
        'reconciliations',
        savedReconciliation.year,
      ]
      const previousState = queryClient.getQueryData(
        queryKey,
      ) as Reconciliation[]
      const newState = [...previousState]

      newState.splice(
        previousState.findIndex(
          (reconciliation) =>
            reconciliation.spend_id === savedReconciliation.spend_id,
        ),
        1,
        savedReconciliation,
      )

      queryClient.setQueryData(queryKey, newState)

      return previousState
    },
  })
}
